import { useEffect, useState } from "react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Cell } from "recharts";
import { api } from "../../api";
import { APageHead, AMetric, ATable, ABadge } from "../adminKit";

const COLORS = ["#3B82F6", "#8B5CF6", "#06b6d4"];
const tip = { background: "#12121a", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 12, color: "#fff", fontSize: 12 };

export default function AdminBilling() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/admin/billing").then(({ data }) => setD(data)); }, []);
  if (!d) return <div className="text-slate-500">Loading…</div>;
  return (
    <div className="space-y-5">
      <APageHead title="Billing & Revenue" subtitle="Revenue metrics and billing events. Stripe webhooks are supported by the architecture and ready to connect." />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <AMetric label="MRR" value={`$${d.mrr}`} tone="green" />
        <AMetric label="ARR (est.)" value={`$${d.arr}`} tone="green" />
        <AMetric label="ARPU" value={`$${d.arpu}`} tone="blue" />
        <AMetric label="Churn Rate" value={`${d.churn_rate}%`} tone="amber" />
        <AMetric label="New Subscriptions" value={d.new_subscriptions} />
        <AMetric label="Cancellations" value={d.cancellations} tone="red" />
        <AMetric label="Failed Payments" value={d.failed_payments} tone="red" />
        <AMetric label="Refunds" value={d.refunds} />
      </div>

      <div className="grid lg:grid-cols-2 gap-5">
        <div className="cc-card rounded-2xl p-5">
          <h3 className="cc-heading text-base font-500 text-white mb-4">Revenue by Plan</h3>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={d.revenue_by_plan}>
              <XAxis dataKey="name" tick={{ fill: "#64748b", fontSize: 11 }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fill: "#64748b", fontSize: 11 }} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={tip} cursor={{ fill: "rgba(255,255,255,0.03)" }} />
              <Bar dataKey="value" radius={[6, 6, 0, 0]}>{d.revenue_by_plan.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}</Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="cc-card rounded-2xl p-5">
          <h3 className="cc-heading text-base font-500 text-white mb-3">Supported Stripe Webhooks</h3>
          <div className="flex flex-wrap gap-2">
            {d.webhook_support.map((w) => <span key={w} className="rounded-full border border-white/10 cc-surface px-3 py-1.5 text-xs text-slate-300 font-mono">{w}</span>)}
          </div>
          <p className="text-xs text-slate-500 mt-4">Secret payment credentials are never exposed to the frontend.</p>
        </div>
      </div>

      <div className="cc-card rounded-2xl p-5">
        <h3 className="cc-heading text-base font-500 text-white mb-3">Billing Events</h3>
        <ATable loading={false} rows={d.events} columns={[
          { key: "number", label: "ID", render: (r) => <span className="font-mono text-xs">{r.number}</span> },
          { key: "event_type", label: "Type", render: (r) => <ABadge tone="blue">{r.event_type}</ABadge> },
          { key: "plan", label: "Plan" },
          { key: "amount", label: "Amount", render: (r) => `$${r.amount}` },
          { key: "status", label: "Status", render: (r) => <ABadge tone="green">{r.status}</ABadge> },
          { key: "provider", label: "Provider" },
          { key: "created_at", label: "Date", render: (r) => new Date(r.created_at).toLocaleDateString() },
        ]} empty="No billing events yet." />
      </div>
    </div>
  );
}
